/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { get } from "lodash";
import { Box, Row, Col } from "jsxstyle";
import useForm from "hooks/useForm";
import useAsync from "hooks/useAsync";
import useToast from "hooks/useToast";
import isRequired from "validators/isRequired";
import saveCMBSSellerService from "services/cmbs/saveCMBSSellerService";
import Form from "components/Form";
import Button from "components/Button";
import FormGrid from "components/FormGrid";
import FormGroup from "components/FormGroup";
import Separator from "components/Separator";
import CurrencyFormat from "components/CurrencyFormat";
import CurrencyInputGroup from "components/CurrencyInputGroup";
import SelectEntityMaster from "components/SelectEntityMaster";

function CMBSSellersForm({
  onData,
  initialValues,
  onCancel,
  dealSize,
  sellerCredit,
}) {
  const { showSuccess, showError } = useToast();

  const {
    getValue,
    setValue,
    getValues,
    setValues,
    getError,
    submit,
    reset,
    clear,
  } = useForm({
    onSubmit: (values) =>
      fetch({
        values,
        dealCode: values.dealCode,
        sellerId: values.id,
      }),
    initialValues: {
      ...(initialValues || {}),
      amount_percentage: Number(
        (((initialValues.amount || 0) / dealSize) * 100).toFixed(2),
      ),
    },
    validations: {
      seller_entity_id: [isRequired],
    },
  });

  const [fetch, isSubmitting] = useAsync({
    promiseFn: saveCMBSSellerService,
    onData: () => {
      showSuccess("Seller saved.");
      reset();
      onData();
    },
    onError: (error) => {
      const type = get(error, "data.error");
      showError(
        {
          duplicated_key: "Cannot add same Seller twice to the same deal",
        }[type] || "An error happened.",
      );
    },
  });

  const handleCancel = React.useCallback(() => {
    onCancel();
    reset();
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEditing = Boolean(getValue("id"));

  return (
    <Form onSubmit={() => submit()}>
      <FormGrid>
        <Box className="col-6">
          <FormGroup
            label="Seller"
            disabled={isSubmitting}
            error={getError("seller_entity_id")}
          >
            <SelectEntityMaster
              onSelect={(value) => setValue("seller_entity_id", value)}
              value={getValue("seller_entity_id")}
              error={getError("seller_entity_id")}
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("amountKey")}
            label="Amount"
            disabled={isSubmitting}
            error={getError("amount")}
          >
            <CurrencyInputGroup
              leftIcon="dollar"
              intent={getError("amount") ? "danger" : null}
              value={getValue("amount")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: value,
                  amount_percentage: Number(
                    ((value / dealSize) * 100).toFixed(2),
                  ),
                  percentageKey: getValue("percentageKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("percentageKey")}
            label="Amount(%)"
            disabled={isSubmitting}
            error={getError("amount_percentage")}
          >
            <CurrencyInputGroup
              leftIcon="percentage"
              value={getValue("amount_percentage")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: Number(((value * dealSize) / 100).toFixed(2)),
                  amount_percentage: value,
                  amountKey: getValue("amountKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Row className="col-12">
          <Row flex={1} alignItems="flex-start">
            {isEditing && (
              <React.Fragment>
                <Button
                  text="Cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                />
                <Separator />
              </React.Fragment>
            )}
            <Button
              type="submit"
              text={isEditing ? "Save" : "Add"}
              intent="success"
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </Row>
          <Col alignItems="flex-end">
            <Box component="p">
              Deal size:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize} />
              </Box>
            </Box>

            <Box component="p">
              Remaining seller credit:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize - sellerCredit} />
              </Box>
            </Box>
          </Col>
        </Row>
      </FormGrid>
    </Form>
  );
}

CMBSSellersForm.propTypes = {};

CMBSSellersForm.defaultProps = {};

export default CMBSSellersForm;
